import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useBranch } from '@/contexts/BranchContext'
import {
  defaultInsightsPreferences,
  loadInsightsPreferences,
  saveInsightsPreferences,
} from '@/lib/reports/preferences'

/** @type {import('react').Context<ReportPreferencesContextValue | null>} */
const ReportPreferencesContext = createContext(null)

/**
 * @typedef {object} ReportPreferencesContextValue
 * @property {string} period
 * @property {string} compare
 * @property {string} customFrom
 * @property {string} customTo
 * @property {string[]} favourites
 * @property {boolean} favouritesOnly
 * @property {string} category
 * @property {string} search
 * @property {number} expiringWindowDays
 * @property {string} activeReportKey
 * @property {(period: string) => void} setPeriod
 * @property {(compare: string) => void} setCompare
 * @property {(value: string) => void} setCustomFrom
 * @property {(value: string) => void} setCustomTo
 * @property {(category: string) => void} setCategory
 * @property {(search: string) => void} setSearch
 * @property {(only: boolean) => void} setFavouritesOnly
 * @property {(days: number) => void} setExpiringWindowDays
 * @property {(key: string) => void} setActiveReportKey
 * @property {(key: string) => void} toggleFavourite
 * @property {(key: string) => boolean} isFavourite
 * @property {(key: string, meta?: { category?: string, name?: string }) => boolean} isReportVisible
 */

/**
 * @param {{ children: import('react').ReactNode }} props
 */
export function ReportPreferencesProvider({ children }) {
  const { activeBranchId } = useBranch()
  const [prefs, setPrefs] = useState(defaultInsightsPreferences)

  useEffect(() => {
    setPrefs(loadInsightsPreferences(activeBranchId))
  }, [activeBranchId])

  const persist = useCallback(
    (updater) => {
      setPrefs((current) => {
        const next = typeof updater === 'function' ? updater(current) : updater
        saveInsightsPreferences(activeBranchId, next)
        return next
      })
    },
    [activeBranchId],
  )

  const setPeriod = useCallback((period) => persist((p) => ({ ...p, period })), [persist])
  const setCompare = useCallback((compare) => persist((p) => ({ ...p, compare })), [persist])
  const setCustomFrom = useCallback(
    (customFrom) => persist((p) => ({ ...p, customFrom })),
    [persist],
  )
  const setCustomTo = useCallback((customTo) => persist((p) => ({ ...p, customTo })), [persist])
  const setCategory = useCallback((category) => persist((p) => ({ ...p, category })), [persist])
  const setSearch = useCallback((search) => persist((p) => ({ ...p, search })), [persist])
  const setFavouritesOnly = useCallback(
    (favouritesOnly) => persist((p) => ({ ...p, favouritesOnly })),
    [persist],
  )
  const setExpiringWindowDays = useCallback(
    (expiringWindowDays) => persist((p) => ({ ...p, expiringWindowDays })),
    [persist],
  )
  const setActiveReportKey = useCallback(
    (activeReportKey) => persist((p) => ({ ...p, activeReportKey })),
    [persist],
  )

  const toggleFavourite = useCallback(
    (key) => {
      persist((p) => {
        const set = new Set(p.favourites)
        if (set.has(key)) set.delete(key)
        else set.add(key)
        return { ...p, favourites: [...set] }
      })
    },
    [persist],
  )

  const isFavourite = useCallback((key) => prefs.favourites.includes(key), [prefs.favourites])

  const isReportVisible = useCallback(
    (key, meta = {}) => {
      const query = prefs.search.trim().toLowerCase()
      if (prefs.favouritesOnly && !prefs.favourites.includes(key)) return false
      if (prefs.category !== 'all' && meta.category && meta.category !== prefs.category) return false
      if (query) {
        const haystack = `${meta.name ?? ''} ${key} ${meta.category ?? ''}`.toLowerCase()
        if (!haystack.includes(query)) return false
      }
      return true
    },
    [prefs.category, prefs.favourites, prefs.favouritesOnly, prefs.search],
  )

  const value = useMemo(
    () => ({
      ...prefs,
      setPeriod,
      setCompare,
      setCustomFrom,
      setCustomTo,
      setCategory,
      setSearch,
      setFavouritesOnly,
      setExpiringWindowDays,
      setActiveReportKey,
      toggleFavourite,
      isFavourite,
      isReportVisible,
    }),
    [
      prefs,
      setPeriod,
      setCompare,
      setCustomFrom,
      setCustomTo,
      setCategory,
      setSearch,
      setFavouritesOnly,
      setExpiringWindowDays,
      setActiveReportKey,
      toggleFavourite,
      isFavourite,
      isReportVisible,
    ],
  )

  return (
    <ReportPreferencesContext.Provider value={value}>{children}</ReportPreferencesContext.Provider>
  )
}

/** @returns {ReportPreferencesContextValue} */
export function useReportPreferences() {
  const context = useContext(ReportPreferencesContext)
  if (!context) {
    throw new Error('useReportPreferences must be used within ReportPreferencesProvider')
  }
  return context
}

/** Shared period/compare filters for all report panels. */
export function useReportPanelFilters() {
  const {
    period,
    setPeriod,
    compare,
    setCompare,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
  } = useReportPreferences()

  return {
    period,
    setPeriod,
    compare,
    setCompare,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
  }
}
